/**
 * collection-nav.js
 *
 * Custom element for the collection page navigation.
 * Handles:
 *  - SORT panel toggle (mobile)
 *  - YEAR dropdown expand/retract (both breakpoints)
 *  - Pre-selects latest month when year group is chosen
 *  - On first YEAR open, auto-navigates to the latest month link
 */

if (!customElements.get('collection-nav')) {
  customElements.define('collection-nav', class CollectionNav extends HTMLElement {
    constructor() {
      super();

      // SORT toggle
      this.sortTrigger = this.querySelector('.collection-nav__sort-trigger');
      if (this.sortTrigger) {
        this.sortTrigger.addEventListener('click', this.toggleSort.bind(this));
      }

      // YEAR toggle(s) — any button with data-has-children
      this.yearToggles = this.querySelectorAll('[data-has-children]');
      this.yearToggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleSubmenu(toggle);
        });
      });

      // Year group toggles — any button with data-has-grandchildren
      this.yearGroupToggles = this.querySelectorAll('[data-has-grandchildren]');
      this.yearGroupToggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleMonths(toggle);
        });
      });

      // On page load: open tiers if there's already an active child
      this.activateLatestMonthPanel();
    }

    toggleSort() {
      const isOpen = this.getAttribute('data-sort-open') === 'true';
      this.setAttribute('data-sort-open', !isOpen);
      this.sortTrigger.setAttribute('aria-expanded', !isOpen);
    }

    /**
     * Toggle the Tier 2 submenu (year groups row) ONLY.
     * Tier 3 (months) stays untouched when closing.
     * On first open (when no months are active), auto-selects latest month.
     */
    toggleSubmenu(toggle) {
      const submenuId = toggle.getAttribute('aria-controls');
      const submenu = this.querySelector(`#${submenuId}`);
      if (!submenu) return;

      const isOpen = submenu.classList.contains('is-open');

      // Only close Tier 2 (year groups) — leave Tier 3 (months) untouched
      this.querySelectorAll('.collection-nav__submenu').forEach(sm => {
        sm.classList.remove('is-open');
      });
      this.yearToggles.forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        // Open Tier 2
        submenu.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        toggle.classList.add('is-active');

        // Check if any month panel is already open
        const alreadyOpenMonths = this.querySelector('.collection-nav__months.is-open');

        if (!alreadyOpenMonths) {
          // First open — auto-select the latest year group and navigate to its latest month
          this.autoSelectLatestMonth(submenu);
        }
      }
    }

    /**
     * Find the latest year group in the submenu, open its months panel,
     * and programmatically click the last month link to load content.
     */
    autoSelectLatestMonth(submenu) {
      const yearGroups = submenu.querySelectorAll('[data-has-grandchildren]');
      if (yearGroups.length === 0) return;

      // Pick the last year group (latest)
      const latestGroup = yearGroups[yearGroups.length - 1];
      const monthsId = latestGroup.getAttribute('aria-controls');
      const monthsPanel = this.querySelector(`#${monthsId}`);

      if (monthsPanel) {
        monthsPanel.classList.add('is-open');
        latestGroup.classList.add('is-active');
        latestGroup.setAttribute('aria-expanded', 'true');

        // Panels are now open — user can manually choose a month
      }
    }

    /**
     * On page load: if we're already on a page within the YEAR tree,
     * open Tier 2 + the latest Tier 3 months panel with an active child.
     */
    activateLatestMonthPanel() {
      // Find all months panels that have an active child link
      const activePanels = this.querySelectorAll('.collection-nav__months[data-has-active-child="true"]');
      if (activePanels.length === 0) return;

      // Only open the last one (latest year group)
      const latestPanel = activePanels[activePanels.length - 1];
      latestPanel.classList.add('is-open');

      // Mark the corresponding year-group toggle as active
      const panelId = latestPanel.id;
      const yearGroupToggle = this.querySelector(`[aria-controls="${panelId}"]`);
      if (yearGroupToggle) {
        yearGroupToggle.classList.add('is-active');
        yearGroupToggle.setAttribute('aria-expanded', 'true');
      }

      // Also open the parent Tier 2 submenu (year groups row)
      const activeSubmenus = this.querySelectorAll('.collection-nav__submenu[data-has-active-child="true"]');
      if (activeSubmenus.length > 0) {
        const latestSubmenu = activeSubmenus[activeSubmenus.length - 1];
        latestSubmenu.classList.add('is-open');

        // Mark the YEAR toggle as active too
        const submenuId = latestSubmenu.id;
        const yearToggle = this.querySelector(`[aria-controls="${submenuId}"]`);
        if (yearToggle) {
          yearToggle.classList.add('is-active');
          yearToggle.setAttribute('aria-expanded', 'true');
        }
      }
    }

    toggleMonths(toggle) {
      const monthsId = toggle.getAttribute('aria-controls');
      const months = this.querySelector(`#${monthsId}`);
      if (!months) return;

      const isOpen = months.classList.contains('is-open');

      // Close all month panels first
      this.querySelectorAll('.collection-nav__months').forEach(m => {
        m.classList.remove('is-open');
      });
      this.querySelectorAll('[data-has-grandchildren]').forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-expanded', 'false');
      });

      // Toggle this one
      if (!isOpen) {
        months.classList.add('is-open');
        toggle.classList.add('is-active');
        toggle.setAttribute('aria-expanded', 'true');
      }
    }
  });
}
